import * as _ from 'lodash';
import * as moment from 'moment';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BreadcrumbsService } from '../../core/breadcrumbs.service';
import { IAccountType } from '../../constants/account-types';
import { LoginGuardService } from '../../api/login-guard.service';
import { LangService } from '../../core/lang.service';
import { InstMngView } from '../pnel-my-institution/pnel-my-institution.component';
import { _SAMPLE_INVITATION_CODES } from '../../constants/fakeinvitations/codes';
import { FormControl, FormGroup } from '@angular/forms';
import { applySecretKeyMask } from '../../core/secret-key-mask';
import { Subscription } from 'rxjs';
import { DemoDataService } from '../demo-data.service';
import { MyInstitutionService, IInstitutionInfo } from '../my-institution.service';
import { AuthService } from '../../api/auth.service';


export interface IAccountInfo {
  id: number,
  __invitationExpiresOn: moment.Moment,
  __isSelected: FormControl,
  isSelf?:boolean,
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  isAccommCoord: boolean,
  numSessions: number,
  accountCreatedOn: moment.Moment,
  invitationCode: string,
  invitationName: string,
  invitationEmail: string,
  accessGrantedOn: moment.Moment,
  isInviteUsed: boolean,
}

enum AccountModal { 
  ACCOUNT_DETAILS = 'account-details',
  NEW_ACCOUNT     = 'new-account',
  ASSIGN_ACCOMM   = 'assign-accomm',
  UNASSIGN_ACCOMM = 'unassign-accomm',
  REASSIGN_ACCOMM = 'reassign-accomm',
  REVOKE_ACCESS   = 'revoke-access',
}

interface IActiveModal {
  type: AccountModal,
  config: any,
  finish: (config:any)=>void,
  confirmationCaption?:string,
}

interface IModalAccount {
  account: IAccountInfo,
}
interface IModalNewAccount {
  invitationInfo: FormGroup,
}
interface IModalAssignAccom {
  newAccount: IAccountInfo,
}
interface IModalReassignAccom {
  oldAccount: IAccountInfo,
  newAccount: IAccountInfo,
}
interface IModalUnassignAccom {
  oldAccount: IAccountInfo,
}
interface IModalRevokeAccess {
  selectedAccounts: IAccountInfo[],
}

@Component({
  selector: 'view-mng-inst-accounts',
  templateUrl: './view-mng-inst-accounts.component.html',
  styleUrls: ['./view-mng-inst-accounts.component.scss']
})
export class ViewMngInstAccountsComponent implements OnInit, OnDestroy {

  public breadcrumb = [];
  public isLoaded:boolean;
  public isLoadFailed:boolean;
  public accounts:IAccountInfo[] = [ ];
  public isAllSelected = new FormControl();
  public currentModal:IActiveModal;
  public accommCoord:IAccountInfo;
  public filterToPending:boolean;
  public AccountModal = AccountModal;
  public isAnyAccountSelected:boolean;
  public isAnyPendingInvitations:boolean;
  
  private institutionInfo:IInstitutionInfo
  private accountSelectSubs: Subscription[] = [];

  constructor(
    public lang:LangService,
    private loginGuard: LoginGuardService, // 
    private breadcrumbsService: BreadcrumbsService,
    private router:Router,
    private demoData:DemoDataService,
    private myInst: MyInstitutionService,
    private auth: AuthService,
  ) { }
  
  ngOnInit() {
    this.loginGuard.activate([IAccountType.TEST_ADMIN]);
    this.breadcrumb = [
      this.breadcrumbsService.TESTADMIN_LANDING(),
      this.breadcrumbsService.TESTADMIN_DASHBOARD(),
      this.breadcrumbsService._CURRENT(this.lang.tra('ta_title_mng_accounts'), this.router.url),
    ];
    this.initRouteView()
  }

  ngOnDestroy() {
    this.accountSelectSubs.forEach(sub => sub.unsubscribe() );
  }
  
  isInited:boolean;
  initRouteView(){
    this.auth.user().subscribe(userInfo => {
      if (userInfo && !this.isInited){
        this.isInited = true;
        this.myInst.sub().subscribe(institutionInfo => {
          this.institutionInfo = institutionInfo;
        });
        this.isAllSelected.valueChanges.subscribe(isAllSelected => {
          this.accounts.forEach(account => {
            if (!account.isSelf){
              account.__isSelected.setValue(isAllSelected);
            }
          })
        })
        this.initSampleData();
      }
    });
  }

  initSampleData(){
    this.isLoaded = true;
    this.accounts = [];
    let isAnyPendingInvitations = false;
    this.demoData.getAccounts().forEach(accountInfo => {
      const account = this.defineAccount(accountInfo);
      if (account.isAccommCoord){
        this.accommCoord = account;
      }
      if (!account.isInviteUsed){
        isAnyPendingInvitations = true;
      }
    })
    this.isAnyPendingInvitations = isAnyPendingInvitations;
    this.demoData.overrideAccountData(this.accounts); // TO DO : replace with db store
  }

  defineAccount(accountInfo:Partial<IAccountInfo>){
    const selectionCheckbox = new FormControl(false);
    this.accountSelectSubs.push(
      selectionCheckbox.valueChanges.subscribe( v => this.checkAllSelected() )
    )
    accountInfo.__isSelected = selectionCheckbox;
    const account = <IAccountInfo>accountInfo;
    this.accounts.push(account);
    this.demoData.overrideAccountData(this.accounts);
    return account;
  }

  getFacultyName(){
    if (!this.institutionInfo){
      return '';
    }
    return this.institutionInfo.name;
  }

  closeModal = (config?:any) => {
    this.currentModal = null;
  }

  confirmModal(){
    this.currentModal.finish(this.currentModal.config);
  }

  newModal = (modalConfig:IActiveModal) => {
    this.currentModal = modalConfig;
  }

  // accountDetail
  accountDetailModalStart(account:IAccountInfo){
    const config:IModalAccount = { account }
    this.newModal({
      type: AccountModal.ACCOUNT_DETAILS, 
      config, 
      finish: this.closeModal,
    });
  }

  // newAccount
  newAccountModalStart(){
    const config:IModalNewAccount = { 
      invitationInfo: new FormGroup({
        name: new FormControl(''),
        email: new FormControl(''),
        isAccomm: new FormControl(false),
        sendEmail: new FormControl(true),
      })
    }

    this.newModal({
      type: AccountModal.NEW_ACCOUNT, 
      config, 
      finish: this.newAccountModalFinish,
      confirmationCaption: this.lang.tra('btn_create_invi')
    });
  }
  newAccountModalFinish = (config:IModalNewAccount) => {
    const controls = config.invitationInfo.controls;
    const account = this.defineAccount({
      isAccommCoord: false,
      numSessions: 0,
      invitationCode: this.demoData.getInvitationCode(), // temp
      invitationName: controls.name.value,
      invitationEmail: controls.email.value,
      accessGrantedOn: moment(),
      __invitationExpiresOn: moment().add(3, 'days'),
      isInviteUsed: false,
    })
    this.isAnyPendingInvitations = true;
    const isAccomm = controls.isAccomm.value;
    this.closeModal();
    if (isAccomm){
      this.setAccomm(account);
    }
  }

  // accommAssign
  accommAssignModalStart(newAccount: IAccountInfo){
    const config:IModalAssignAccom = { newAccount }
    this.newModal({
      type: AccountModal.ASSIGN_ACCOMM, 
      config, 
      finish: this.accommAssignModalFinish
    });
  }
  accommAssignModalFinish = (config:IModalAssignAccom) => {
    this.assignAccommCoord(config.newAccount);
    this.closeModal();
  }

  

  // accommReassign
  accommReassignModalStart(oldAccount: IAccountInfo, newAccount: IAccountInfo){
    const config:IModalReassignAccom = { oldAccount, newAccount } 
    this.newModal({
      type: AccountModal.REASSIGN_ACCOMM, 
      config, 
      finish: this.accommReassignModalFinish
    });
  }
  accommReassignModalFinish = (config:IModalReassignAccom) => {
    this.unassignAccommCoord(config.oldAccount);
    this.assignAccommCoord(config.newAccount);
    this.closeModal();
  }

  // accommReassign
  accommUnassignModalStart(oldAccount: IAccountInfo){
    const config:IModalUnassignAccom = { oldAccount } 
    this.newModal({
      type: AccountModal.UNASSIGN_ACCOMM, 
      config, 
      finish: this.accommUnassignModalFinish
    });
  }
  accommUnassignModalFinish = (config:IModalUnassignAccom) => {
    this.unassignAccommCoord(config.oldAccount);
    this.closeModal();
  }

  // revokeAccess
  revokeAccessModalStart(){
    const selectedAccounts = [];
    this.accounts.forEach(account => {
      if (account.__isSelected.value){
        selectedAccounts.push(account);
      }
    })
    // get selected accounts
    const config:IModalRevokeAccess = { selectedAccounts };
    this.newModal({
      type: AccountModal.REVOKE_ACCESS, 
      config, 
      finish: this.revokeAccessModalFinish
    });
  }
  revokeAccessModalFinish = (config:IModalRevokeAccess) => {
    config.selectedAccounts.forEach(account => {
      this.removeAccountFromList(account);
    })
    this.closeModal();
  }

  removeAccountFromList(account: IAccountInfo){
    let i = -1;
    this.accounts.forEach( (_account, _i) => {
      if(account === _account){
        i = _i;
      }
    })
    if (i !== -1){
      this.accounts.splice(i, 1);
    }
    this.isAllSelected.setValue(false);
  }

  // coord
  isAccommCoordAssigned(){
    return !!this.accommCoord;
  }
  isAccommCoord(acct:IAccountInfo){
    return (this.accommCoord === acct);
  }
  unassignAccommCoord(acct:IAccountInfo){
    if (acct !== this.accommCoord){
      console.warn( this.lang.tra('txt_create_invi_error') );
    }
    if (this.accommCoord){
      this.accommCoord.isAccommCoord = false;  
    }
    this.accommCoord = null;
  }
  assignAccommCoord(acct:IAccountInfo){
    this.accommCoord = acct;
    this.accommCoord.isAccommCoord = true;
  }
  setAccomm(acct:IAccountInfo){
    if (this.accommCoord){
      this.accommReassignModalStart(this.accommCoord, acct);
    }
    else{
      this.accommAssignModalStart(acct);
    }
  }
  unsetAccomm(acct:IAccountInfo){
    this.accommUnassignModalStart(acct);
  }

  checkAllSelected = _.debounce(() => {
    let isAnyAccountSelected = false;
    this.accounts.forEach(account => {
      if (account.__isSelected.value){
        isAnyAccountSelected = true;
      }
    })
    this.isAnyAccountSelected = isAnyAccountSelected;
  }, 50)

  renderTimestamp(m:moment.Moment){
    return m.format('MMM D, YYYY H:mma');
  }

  renderAccountName(account: IAccountInfo){
    if (account.isInviteUsed){
      return account.firstName + ' ' + account.lastName;
    }
    else{
      return account.invitationName;
    }
  }

  renderBracketedAccountEmail(account: IAccountInfo){
    const str = this.renderAccountEmail(account);
    if (str){
      return '('+str+')';
    }
    return '';
  }
  renderAccountEmail(account: IAccountInfo){
    if (account.isInviteUsed){
      return account.email;
    }
    else{
      return account.invitationEmail;
    }
  }

  prevPage(){
    
  }
  nextPage(){

  }
  isOnFirstPage(){
    return (this.getCurrentPageNum() === 1);
  }
  isOnLastPage(){
    return (this.getCurrentPageNum() === this.getMaxPages());
  }

  getCurrentPageNum(){
    return 1;
  }

  isInputError(){
    
  }

  getMaxPages(){
    return 1;
  }

  getCurrentInstMngView(){
    return InstMngView.ACCOUNTS;
  }

}
